import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Shield, Clock, Users, CheckCircle, Building2, Factory, Lightbulb, Award, Quote, Phone, Mail, MapPin, Send } from 'lucide-react';
import { heroSlider } from '../config/heroSlider';
import { clients } from '../config/clients';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Client Logo Component with fallback and tooltip
const ClientLogo = ({ client }) => {
  const [imageError, setImageError] = useState(false);
  const logoPath = `${process.env.PUBLIC_URL}/assets/clients/${client.fileName}`;

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div className="relative group/tooltip">
      {!imageError ? (
        <img
          src={logoPath}
          alt={client.name}
          style={{
            maxHeight: '70px',
            maxWidth: '180px',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            display: 'block',
            filter: 'none',
            opacity: 1
          }}
          onError={handleImageError}
        />
      ) : (
        <div className="text-center flex flex-col items-center gap-1">
          <Award className="w-8 h-8 text-slate-400 group-hover:text-teal-600 transition-colors" />
          <div className="text-sm font-semibold text-slate-700 group-hover:text-teal-600 transition-colors leading-tight">
            {client.name}
          </div>
        </div>
      )}
      {/* Tooltip */}
      {client.projectType && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover/tooltip:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
          {client.projectType}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
        </div>
      )}
    </div>
  );
};

// Animated Counter Component
const AnimatedCounter = ({ target, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const numericTarget = parseInt(target.replace(/\D/g, '')) || 0;
          const startTime = Date.now();
          
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easeOut = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(numericTarget * easeOut));
            
            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [target, duration, hasAnimated]);

  return <span ref={ref}>{count}{suffix}</span>;
};

export const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formData, setFormData] = useState({ name: '', company: '', projectType: '', phone: '', message: '' });
  const [formStatus, setFormStatus] = useState('');

  const projectTypes = [
    'Solar EPC',
    'Wind EPC',
    'BESS / Energy Storage',
    'Substation & Switchyard',
    'HT/LT Electrical Installation',
    'Transmission Line',
    'Control & Instrumentation (C&I)',
    'Plant & Machinery Erection',
    'O&M Services',
    'Other'
  ];

  // Get image URL based on slide type (local or external)
  const getSlideUrl = (slide) => {
    if (slide.type === 'local') {
      return `${process.env.PUBLIC_URL}/assets/hero/${slide.fileName}`;
    }
    return slide.url;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlider.slides.length);
    }, heroSlider.interval);
    return () => clearInterval(interval);
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setFormStatus('sent');
        setFormData({ name: '', company: '', projectType: '', phone: '', message: '' });
        setTimeout(() => setFormStatus(''), 5000);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
      setFormStatus('');
    }
  };

  const services = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'EPC Contracts',
      description: 'Turnkey electrical and instrumentation solutions from design to commissioning.',
      image: 'https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=400&h=250&fit=crop',
      link: '/services#epc'
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: 'Renewable Energy',
      description: 'Complete solar and renewable energy project execution — from equipment installation and testing to commissioning and statutory approvals.',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=400&h=250&fit=crop',
      link: '/services#renewable'
    },
    {
      icon: <Factory className="w-8 h-8" />,
      title: 'Substation Works',
      description: 'Switchyard and substation projects with Power Substation System (PSS) execution up to 220 kV.',
      image: 'https://images.unsplash.com/photo-1509390484649-e8f3d1bb0b6d?w=400&h=250&fit=crop',
      link: '/services#substation'
    },
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: 'Transmission Lines',
      description: 'Transmission line projects up to 400 kV with complete erection and commissioning.',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=400&h=250&fit=crop',
      link: '/services#transmission'
    }
  ];

  const whyChooseUs = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Safety First',
      description: 'Compliance-focused approach with statutory standards'
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: 'Quality Driven',
      description: 'End-to-end project execution with quality assurance'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'On-Time Delivery',
      description: 'Disciplined project management ensuring timely completion'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Expert Team',
      description: 'Capable engineers and skilled workforce'
    }
  ];

  const stats = [
    { number: '5+', label: 'Years of Excellence', target: '5', suffix: '+' },
    { number: '90+', label: 'Projects Delivered', target: '90', suffix: '+' },
    { number: '220', label: 'kV Max Capacity', target: '220', suffix: 'kV' },
    { number: 'PAN', label: 'India Coverage', target: '', suffix: 'PAN India' }
  ];

  const projectHighlights = [
    {
      title: '220kV Substation — Karnataka',
      description: 'Complete switchyard and PSS execution',
      image: 'https://customer-assets.emergentagent.com/job_ed6e21f6-4588-40b2-8743-fbb7a90d8f8d/artifacts/3skwyyzm_a04216058599091312f0df88b58f54bb.jpg',
      category: 'Substation'
    },
    {
      title: 'Solar Plant BESS — Karnataka',
      description: '4MW/10MWh Battery Energy Storage System installation',
      image: 'https://customer-assets.emergentagent.com/job_ed6e21f6-4588-40b2-8743-fbb7a90d8f8d/artifacts/xe6wu0mp_BESS%201.jpg',
      category: 'Renewable Energy'
    },
    {
      title: '400kV Transmission Line — Maharashtra',
      description: 'Complete tower erection and stringing works',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=400&h=300&fit=crop',
      category: 'Transmission'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Calspark Electrical & Engineers | EPC Contractor India"
        description="Calspark Electrical & Engineers is a trusted EPC contractor specialising in high-voltage switchyards, transmission lines, renewable energy, and industrial power projects across Pan India."
        keywords="EPC contractor India, electrical contractor, switchyard erection, transmission line, renewable energy EPC, 132KV 220KV substation, Karnataka"
        ogUrl="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Sliding Background Images with increased brightness */}
        <div className="absolute inset-0 z-0">
          {heroSlider.slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={getSlideUrl(slide)}
                alt={slide.alt}
                className="w-full h-full object-cover"
                style={{ filter: 'brightness(1.15) contrast(1.1)' }}
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-slate-900/65"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-32 text-center text-white">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            Powering Progress
            <span className="block text-teal-400 mt-2">with Precision</span>
          </h1>
          
          <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
            Delivering turnkey EPC solutions across India with a focus on safety, quality, and on-time project execution. Your trusted partner in electrical and instrumentation contracting.
          </p>

          {/* Stats with Animated Counters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6">
                <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">
                  {stat.target ? <AnimatedCounter target={stat.target} suffix={stat.suffix} /> : stat.suffix}
                </div>
                <div className="text-sm text-slate-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Slider Progress Indicator */}
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
          {heroSlider.slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-teal-400 w-8' 
                  : 'bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Services Overview with Images */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">Our Core Services</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive EPC solutions for diverse industrial and infrastructure projects
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                {/* Service Image */}
                <div className="relative h-40 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                  <div className="absolute bottom-3 left-3 text-white">
                    {service.icon}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">{service.title}</h3>
                  <p className="text-slate-600 leading-relaxed text-sm mb-4">{service.description}</p>
                  <Link 
                    to={service.link}
                    className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm hover:gap-2 transition-all"
                  >
                    Learn More <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-teal-600 font-medium hover:gap-4 transition-all duration-300"
            >
              View All Services
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us with Testimonial */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Why Choose Calspark?
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                We are more than a contractor—we are your partner in delivering reliable EPC outcomes. Our commitment to excellence, safety, and customer satisfaction sets us apart.
              </p>

              <div className="space-y-6">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center text-teal-600">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800 mb-1">{item.title}</h3>
                      <p className="text-slate-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/about"
                className="inline-flex items-center gap-2 mt-8 bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors"
              >
                Learn More About Us
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_ed6e21f6-4588-40b2-8743-fbb7a90d8f8d/artifacts/himxvb85_CE%20Team.jpg"
                alt="Calspark Team at Site"
                className="rounded-2xl shadow-2xl object-cover w-full h-auto"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="text-4xl font-bold text-teal-600">100%</div>
                <div className="text-slate-600 font-medium">Customer Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Highlights Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">Our Projects</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Delivering excellence across diverse EPC projects throughout India
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {projectHighlights.map((project, index) => (
              <div key={index} className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="bg-teal-600 text-white text-xs px-3 py-1 rounded-full">
                      {project.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{project.title}</h3>
                  <p className="text-slate-600 text-sm mb-4">{project.description}</p>
                  <Link 
                    to="/projects"
                    className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm hover:gap-2 transition-all"
                  >
                    View Project <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors"
            >
              View All Projects
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Recognition & Licensing Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-4">Recognition & Licensing</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Recognition Certificate Card */}
            <div className="bg-white rounded-xl shadow-lg border-2 border-slate-200 p-8 text-center hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-amber-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-10 h-10 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Recognition Certificate</h3>
              <p className="text-slate-600 text-sm">Private Industry Recognition</p>
            </div>
            
            {/* Government License Card */}
            <div className="bg-white rounded-xl shadow-lg border-2 border-slate-200 p-8 text-center hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-teal-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-10 h-10 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Electrical Contractor License</h3>
              <p className="text-slate-600 text-sm">Issued by: Government Authority</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Clients Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">Trusted By</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Join 20+ industry leaders who trust Calspark for their power and infrastructure projects
            </p>
          </div>

          {/* Logo Carousel */}
          <div className="relative overflow-hidden">
            <div className="flex animate-scroll" style={{ width: 'max-content' }}>
              {/* First set of logos */}
              {clients.map((client, index) => (
                <div
                  key={`client-1-${index}`}
                  className="flex-shrink-0 px-3 sm:px-5"
                >
                  <div 
                    className="group bg-slate-50 rounded-lg border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all duration-300"
                    style={{
                      height: '110px',
                      width: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '16px 20px'
                    }}
                  >
                    <ClientLogo client={client} />
                  </div>
                </div>
              ))}
              {/* Duplicate set for seamless loop */}
              {clients.map((client, index) => (
                <div
                  key={`client-2-${index}`}
                  className="flex-shrink-0 px-3 sm:px-5"
                >
                  <div 
                    className="group bg-slate-50 rounded-lg border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all duration-300"
                    style={{
                      height: '110px',
                      width: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '16px 20px'
                    }}
                  >
                    <ClientLogo client={client} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with Contact Form */}
      <section className="py-20 bg-gradient-to-br from-slate-800 to-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Power Your Next Project?
            </h2>
            <p className="text-xl text-slate-300 mb-6">
              Get in touch with our team for expert consultation and turnkey EPC solutions
            </p>
            
            {/* Trust Signals */}
            <p className="text-sm text-slate-400 mb-8">
              ✓ Free consultation · ✓ Response within 24 hours · ✓ No obligation
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link
                to="/contact"
                className="bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-teal-700 transition-all duration-300 shadow-lg"
              >
                Request a Quote
              </Link>
              <a
                href="tel:+918147451515"
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-white/20 transition-all duration-300 border border-white/20 flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Call Us Now
              </a>
            </div>
          </div>

          {/* Inline Contact Form */}
          <div className="max-w-2xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
            <h3 className="text-xl font-semibold mb-6 text-center">Quick Enquiry</h3>
            {formStatus === 'sent' ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-teal-400 mx-auto mb-4" />
                <h4 className="text-xl font-semibold mb-2">Thank You!</h4>
                <p className="text-slate-300">We respond to all inquiries within 4 business hours. Your information is kept confidential.</p>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Full Name *"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:border-teal-400 transition-colors"
                  />
                  <input
                    type="text"
                    placeholder="Company Name"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <select
                    value={formData.projectType}
                    onChange={(e) => setFormData({...formData, projectType: e.target.value})}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-teal-400 transition-colors appearance-none"
                    style={{ color: formData.projectType ? 'white' : '#94a3b8' }}
                  >
                    <option value="" className="bg-slate-800 text-slate-400">Project Type *</option>
                    {projectTypes.map((type) => (
                      <option key={type} value={type} className="bg-slate-800 text-white">{type}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    placeholder="Phone / WhatsApp *"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>
                <textarea
                  placeholder="Your Message *"
                  rows="3"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:border-teal-400 transition-colors resize-none"
                ></textarea>
                <button
                  type="submit"
                  disabled={formStatus === 'sending'}
                  className="w-full bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {formStatus === 'sending' ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Enquiry
                    </>
                  )}
                </button>
                <p className="text-center text-sm text-slate-400 mt-4">
                  We respond to all inquiries within 4 business hours. Your information is kept confidential.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
